import React from "react";
import {
  Facebook,
  Twitter,
  Mail,
  MapPin,
  Phone,
  Linkedin,
  Youtube,
} from "lucide-react";
import { Link } from "react-router-dom";

// Component Link cho Footer
const FooterLink = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => (
  <Link
    to={href}
    className="text-xs md:text-sm text-foreground/80 hover:text-secondary dark:hover:text-muted transition-colors duration-[--duration-normal] inline-block mb-1.5 md:mb-2"
  >
    {children}
  </Link>
);

// Component Icon Mạng xã hội
const SocialIcon = ({
  icon,
  href,
}: {
  icon: React.ReactNode;
  href: string;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="bg-primary/10 hover:bg-primary/20 p-1.5 md:p-2 rounded-full transition-colors duration-[--duration-normal] inline-flex items-center justify-center text-foreground"
  >
    {icon}
  </a>
);

// Component Footer chính
const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="mt-auto bg-background text-foreground pt-6 md:pt-10 pb-4 md:pb-6 px-3 md:px-[--spacing-page-padding] border-t border-border/30">
      <div className="container mx-auto">
        {/* Lưới nội dung Footer */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8">
          {/* Cột Thương hiệu */}
          <div className="col-span-2 md:col-span-1 flex flex-col items-center md:items-start space-y-3 md:space-y-4">
            <div className="flex items-center space-x-2">
              <img
                src="/logo/icon-full-color.png"
                alt="Locuno Logo"
                className="h-6 md:h-8"
              />
              <h2 className="font-primary text-base md:text-lg font-bold">
                Locuno
              </h2>
            </div>
            <p className="text-xs md:text-sm text-foreground/70 max-w-xs text-center md:text-left">
              Ứng dụng SOS và bảo vệ gia đình. Kết nối khẩn cấp, theo dõi an
              toàn, và hỗ trợ 24/7 cho mọi gia đình Việt Nam.
            </p>
            <div className="flex flex-wrap gap-2 md:gap-3 pt-1 md:pt-2 justify-center md:justify-start">
              <SocialIcon
                icon={<Facebook size={16} className="md:w-5 md:h-5" />}
                href="https://www.facebook.com/safelocuno"
              />
              <SocialIcon
                icon={<Twitter size={16} className="md:w-5 md:h-5" />}
                href="https://x.com/safelocuno"
              />
              <SocialIcon
                icon={<Linkedin size={16} className="md:w-5 md:h-5" />}
                href="https://www.linkedin.com/company/safelocuno/"
              />
              <SocialIcon
                icon={
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-4 h-4 md:w-5 md:h-5"
                  >
                    <path d="M16.5 3h-9C6.12 3 5 4.12 5 5.5v13C5 19.88 6.12 21 7.5 21h9c1.38 0 2.5-1.12 2.5-2.5v-13C19 4.12 17.88 3 16.5 3zm-4.5 2.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5zm4 11c0 .28-.22.5-.5.5h-7c-.28 0-.5-.22-.5-.5v-.5c0-.28.22-.5.5-.5h7c.28 0 .5.22.5.5v.5zm0-3c0 .28-.22.5-.5.5h-7c-.28 0-.5-.22-.5-.5V13c0-.28.22-.5.5-.5h7c.28 0 .5.22.5.5v.5z" />
                  </svg>
                }
                href="https://www.tiktok.com/@safelocuno"
              />
              <SocialIcon
                icon={<Youtube size={16} className="md:w-5 md:h-5" />}
                href="https://www.youtube.com/@safelocuno"
              />
            </div>
          </div>

          {/* Cột Links 1 (Tính năng SOS) */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-semibold text-sm md:text-base text-foreground mb-2 md:mb-4 text-center md:text-left">
              Tính năng SOS
            </h3>
            <div className="flex flex-col items-center md:items-start">
              <FooterLink href="/emergency">Cảnh báo khẩn cấp</FooterLink>
              <FooterLink href="/tracking">Theo dõi vị trí</FooterLink>
              <FooterLink href="/contacts">Danh bạ khẩn cấp</FooterLink>
              <FooterLink href="/safe-zones">Vùng an toàn</FooterLink>
              <FooterLink href="/family-chat">Chat gia đình</FooterLink>
            </div>
          </div>

          {/* Cột Links 2 (Hỗ trợ khẩn cấp) */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-semibold text-sm md:text-base text-foreground mb-2 md:mb-4 text-center md:text-left">
              Hỗ trợ khẩn cấp
            </h3>
            <div className="flex flex-col items-center md:items-start">
              <FooterLink href="/emergency-guide">
                Hướng dẫn khẩn cấp
              </FooterLink>
              <FooterLink href="/safety-tips">Mẹo an toàn</FooterLink>
              <FooterLink href="/hotline">Đường dây nóng</FooterLink>
              <FooterLink href="/family-safety">An toàn gia đình</FooterLink>
              <FooterLink href="/report-emergency">Báo cáo khẩn cấp</FooterLink>
            </div>
          </div>

          {/* Cột Links 3 (Về Locuno) */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-semibold text-sm md:text-base text-foreground mb-2 md:mb-4 text-center md:text-left">
              Về Locuno
            </h3>
            <div className="flex flex-col items-center md:items-start">
              <FooterLink href="/vision-mission">Tầm nhìn & Sứ mệnh</FooterLink>
              <FooterLink href="/first-100">100 Gia đình tiên phong</FooterLink>
              <FooterLink href="/health-ai">Health AI Profile</FooterLink>
            </div>
          </div>

          {/* Cột Thông tin liên hệ */}
          <div className="col-span-2 md:col-span-1 flex flex-col items-center md:items-start">
            <h3 className="font-semibold text-sm md:text-base text-foreground mb-2 md:mb-4 text-center md:text-left">
              Liên hệ
            </h3>
            <div className="space-y-2 md:space-y-3 flex flex-col items-center md:items-start">
              <div className="flex items-start space-x-2 md:space-x-3 max-w-xs text-center md:text-left">
                <span className="text-xs md:text-sm text-foreground/70">
                  CÔNG TY TNHH CLOUDJET SOLUTIONS
                </span>
              </div>
              <div className="flex items-start space-x-2 md:space-x-3 max-w-xs text-center md:text-left">
                <MapPin
                  size={16}
                  className="text-secondary mt-0.5 flex-shrink-0 md:w-5 md:h-5"
                />
                <span className="text-xs md:text-sm text-foreground/70">
                  Lầu 2 , Toà nhà F Central, 16A Lê Hồng Phong, Phường 12, Quận
                  10, TP. Hồ Chí Minh, Việt Nam
                </span>
              </div>
              <div className="flex items-center space-x-2 md:space-x-3 text-center md:text-left">
                <span className="text-xs md:text-sm text-foreground/70">
                  Mã số thuế: 0312033924
                </span>
              </div>
              <div className="flex items-start space-x-2 md:space-x-3 max-w-xs text-center md:text-left">
                <span className="text-xs md:text-sm text-foreground/70">
                  Singapore Entity: CLOUDJET SOLUTIONS PTE. LTD.
                </span>
              </div>
              <div className="flex items-center space-x-2 md:space-x-3 text-center md:text-left">
                <span className="text-xs md:text-sm text-foreground/70">
                  UEN: 201708398E
                </span>
              </div>
              <div className="flex items-center space-x-2 md:space-x-3 text-center md:text-left">
                <span className="text-xs md:text-sm text-foreground/70">
                  D-U-N-S: 659404865
                </span>
              </div>
              <div className="flex items-center space-x-2 md:space-x-3 text-center md:text-left">
                <Phone
                  size={16}
                  className="text-secondary flex-shrink-0 md:w-5 md:h-5"
                />
                <span className="text-xs md:text-sm text-foreground/70">
                  028 628 00110
                </span>
              </div>
              <div className="flex items-center space-x-2 md:space-x-3 text-center md:text-left">
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-4 h-4 md:w-5 md:h-5 text-secondary flex-shrink-0"
                >
                  <path d="M16.5 3h-9C6.12 3 5 4.12 5 5.5v13C5 19.88 6.12 21 7.5 21h9c1.38 0 2.5-1.12 2.5-2.5v-13C19 4.12 17.88 3 16.5 3zm-4.5 2.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5zm4 11c0 .28-.22.5-.5.5h-7c-.28 0-.5-.22-.5-.5v-.5c0-.28.22-.5.5-.5h7c.28 0 .5.22.5.5v.5zm0-3c0 .28-.22.5-.5.5h-7c-.28 0-.5-.22-.5-.5V13c0-.28.22-.5.5-.5h7c.28 0 .5.22.5.5v.5z" />
                </svg>
                <span className="text-xs md:text-sm text-foreground/70">
                  Zalo: 08 986 982 88
                </span>
              </div>
              <div className="flex items-center space-x-2 md:space-x-3 text-center md:text-left">
                <Mail
                  size={16}
                  className="text-secondary flex-shrink-0 md:w-5 md:h-5"
                />
                <span className="text-xs md:text-sm text-foreground/70">
                  contact@locuno.com
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Đường phân cách */}
        <div className="border-t border-border/20 my-4 md:my-6"></div>

        {/* Phần Footer dưới cùng */}
        <div className="flex flex-col md:flex-row items-center md:justify-between space-y-2 md:space-y-0">
          <p className="font-primary text-xs md:text-sm text-foreground/60 text-center md:text-left">
            &copy; {currentYear} Locuno. Tất cả các quyền được bảo lưu.
          </p>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            <Link
              to="/privacy"
              className="text-xs md:text-sm text-foreground/60 hover:text-secondary dark:hover:text-muted transition-colors duration-[--duration-normal]"
            >
              Chính sách Riêng tư
            </Link>
            <Link
              to="/terms"
              className="text-xs md:text-sm text-foreground/60 hover:text-secondary dark:hover:text-muted transition-colors duration-[--duration-normal]"
            >
              Điều khoản Dịch vụ
            </Link>
            <Link
              to="/cookies"
              className="text-xs md:text-sm text-foreground/60 hover:text-secondary dark:hover:text-muted transition-colors duration-[--duration-normal]"
            >
              Chính sách Cookie
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
